<script setup lang="ts">
/**
 * Skeleton para MiniGames na tab "Todos" - grupos por tipo de jogo
 * Imita o layout com header (ícone + título) + swiper de cards verticais
 * Cards: 200x217px (mobile) / 215x217px (desktop) - igual MiniGame.vue
 * Uso: <SmarticoApostatudoSkeletonMiniGamesGroupsSkeleton :groups="5" :cards-per-group="3" />
 */

interface Props {
  /** Quantidade de grupos skeleton (tipos de jogos) */
  groups?: number;
  /** Quantidade de cards por grupo */
  cardsPerGroup?: number;
  /** Altura do card (opcional, padrão 217px conforme Figma) */
  cardHeight?: number;
}

withDefaults(defineProps<Props>(), {
  groups: 5,
  cardsPerGroup: 3,
  cardHeight: 217,
});
</script>

<template>
  <div class="minigames-groups-skeleton">
    <div
      v-for="g in groups"
      :key="`group-${g}`"
      class="skeleton-group"
    >
      <!-- Header do grupo (igual PerfilOficialSwiper) -->
      <div class="skeleton-group-header">
        <div class="skeleton-icon" />
        <div class="skeleton-title" />
      </div>

      <!-- Swiper de cards (horizontal scroll) -->
      <div class="skeleton-swiper">
        <div
          v-for="c in cardsPerGroup"
          :key="`card-${g}-${c}`"
          class="skeleton-card"
          :style="{ height: `${cardHeight}px` }"
        >
          <!-- Imagem (topo, quadrada) -->
          <div class="skeleton-card-image" />
          <!-- Título -->
          <div class="skeleton-card-title" />
          <!-- Descrição -->
          <div class="skeleton-card-desc" />
          <!-- Ações: Preço + Botão -->
          <div class="skeleton-card-actions">
            <div class="skeleton-card-price" />
            <div class="skeleton-card-button" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.minigames-groups-skeleton {
  @apply flex flex-col;
}

.skeleton-group {
  @apply mb-6;
}

/* Header do grupo (igual PerfilOficialSwiper) */
.skeleton-group-header {
  @apply flex h-[45px] items-center gap-4 rounded-md mb-4 select-none;
  background: linear-gradient(
    270deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0) 100%
  );
}

.skeleton-icon {
  @apply w-[45px] h-[45px] flex-shrink-0 rounded-lg;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-title {
  @apply h-5 w-24 md:h-6 md:w-32 rounded;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Swiper de cards (horizontal scroll igual PerfilOficialSwiper) */
.skeleton-swiper {
  @apply flex gap-3 overflow-x-auto pb-2;

  /* Esconder scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Card vertical - igual MiniGame.vue (200x217 mobile / 215x217 desktop) */
.skeleton-card {
  @apply flex flex-col items-center flex-shrink-0;
  @apply rounded-[13px];
  /* Mobile: 200px width */
  width: 200px;
  padding: 13px;
  gap: 6px;
  background: #010612;
  border: 1px solid #1d2329;
}

/* Desktop: 215px width */
@media (min-width: 640px) {
  .skeleton-card {
    width: 215px;
  }
}

/* Imagem quadrada no topo (96x96 igual MiniGame.vue) */
.skeleton-card-image {
  @apply flex-shrink-0 rounded-lg;
  width: 96px;
  height: 96px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Título (16px height) */
.skeleton-card-title {
  @apply self-stretch rounded;
  height: 16px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Descrição (2 linhas = 32px height) */
.skeleton-card-desc {
  @apply self-stretch rounded;
  height: 32px;
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.05) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Ações: Preço + Botão lado a lado (igual MiniGame.vue) */
.skeleton-card-actions {
  @apply self-stretch flex justify-center items-stretch;
  gap: 6px;
  height: 36px;
}

/* Badge de Preço */
.skeleton-card-price {
  @apply flex-1 rounded-lg;
  background: rgba(0, 201, 255, 0.1);
}

/* Botão */
.skeleton-card-button {
  @apply flex-1 rounded;
  background: linear-gradient(
    90deg,
    rgba(255, 112, 74, 0.3) 25%,
    rgba(255, 52, 0, 0.4) 50%,
    rgba(255, 112, 74, 0.3) 75%
  );
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
